import { SegmentedControl } from "@mantine/core";
import { useState } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { CompanySettings } from "#modules/company/infra/CompanySettings.dto";
import { Group } from "#shared/view/components/Group";
import { Stack } from "#shared/view/components/Stack";
import { TextInputController } from "#shared/view/designSystem/inputs/TextInputController";

export const OverThicknessFormulas = () => {
  const { t } = useTranslation();
  const { control, setValue } = useFormContext<CompanySettings>();
  const [activeType, setActiveType] = useState("sheet");

  const handleCommaToDecimal = (name: keyof CompanySettings) => (value: string) => {
    if (/^\d+,\d+$/.test(value)) {
      setValue(name as Parameters<typeof setValue>[0], value.replace(",", ".") as never);
    }
  };

  return (
    <Stack gap="sm">
      <SegmentedControl
        value={activeType}
        onChange={setActiveType}
        data={[
          { label: t("settings.materials.sheet"), value: "sheet" },
          { label: t("settings.materials.machining"), value: "machining" },
          { label: t("settings.materials.turning"), value: "turning" },
        ]}
        style={{ width: "fit-content" }}
      />

      {activeType === "sheet" && (
        <>
          <Group>
            <TextInputController
              name="sheetCutWidthFormula"
              control={control}
              label={t("settings.materials.sheetCutWidthFormula")}
              onValueChange={handleCommaToDecimal("sheetCutWidthFormula")}
            />
            <TextInputController
              name="sheetSkeletonWidthFormula"
              control={control}
              label={t("settings.materials.sheetSkeletonWidthFormula")}
              onValueChange={handleCommaToDecimal("sheetSkeletonWidthFormula")}
            />
          </Group>
          <Group>
            <TextInputController
              name="sheetOverThicknessLengthFormula"
              control={control}
              label={t("settings.materials.machiningOverThicknessLengthFormula")}
              onValueChange={handleCommaToDecimal("machiningOverThicknessLengthFormula")}
            />
            <TextInputController
              name="sheetOverThicknessWidthFormula"
              control={control}
              label={t("settings.materials.machiningOverThicknessWidthFormula")}
              onValueChange={handleCommaToDecimal("machiningOverThicknessWidthFormula")}
            />
            <TextInputController
              name="sheetOverThicknessHeightFormula"
              control={control}
              label={t("settings.materials.machiningOverThicknessHeightFormula")}
              onValueChange={handleCommaToDecimal("machiningOverThicknessHeightFormula")}
            />
          </Group>
        </>
      )}

      {activeType === "machining" && (
        <Stack gap="sm">
          <Group>
            <TextInputController
              name="machiningCutWidthFormula"
              control={control}
              label={t("settings.materials.machiningCutWidthFormula")}
              onValueChange={handleCommaToDecimal("machiningCutWidthFormula")}
            />
            <TextInputController
              name="machiningSkeletonWidthFormula"
              control={control}
              label={t("settings.materials.machiningSkeletonWidthFormula")}
              onValueChange={handleCommaToDecimal("machiningSkeletonWidthFormula")}
            />
          </Group>
          <Group>
            <TextInputController
              name="machiningOverThicknessLengthFormula"
              control={control}
              label={t("settings.materials.machiningOverThicknessLengthFormula")}
              onValueChange={handleCommaToDecimal("machiningOverThicknessLengthFormula")}
            />
            <TextInputController
              name="machiningOverThicknessWidthFormula"
              control={control}
              label={t("settings.materials.machiningOverThicknessWidthFormula")}
              onValueChange={handleCommaToDecimal("machiningOverThicknessWidthFormula")}
            />
            <TextInputController
              name="machiningOverThicknessHeightFormula"
              control={control}
              label={t("settings.materials.machiningOverThicknessHeightFormula")}
              onValueChange={handleCommaToDecimal("machiningOverThicknessHeightFormula")}
            />
          </Group>
        </Stack>
      )}

      {activeType === "turning" && (
        <Stack gap="sm">
          <Group>
            <TextInputController
              name="turningCutWidthFormula"
              control={control}
              label={t("settings.materials.turningCutWidthFormula")}
              onValueChange={handleCommaToDecimal("turningCutWidthFormula")}
            />
            <TextInputController
              name="turningChuckGripFormula"
              control={control}
              label={t("settings.materials.turningChuckGripFormula")}
              onValueChange={handleCommaToDecimal("turningChuckGripFormula")}
            />
          </Group>
          <Group>
            <TextInputController
              name="turningOverThicknessLengthFormula"
              control={control}
              label={t("settings.materials.turningOverThicknessLengthFormula")}
              onValueChange={handleCommaToDecimal("turningOverThicknessLengthFormula")}
            />
            <TextInputController
              name="turningOverThicknessDiameterFormula"
              control={control}
              label={t("settings.materials.turningOverThicknessDiameterFormula")}
              onValueChange={handleCommaToDecimal("turningOverThicknessDiameterFormula")}
            />
          </Group>
        </Stack>
      )}
    </Stack>
  );
};
